import { useQuery } from '@tanstack/react-query';
import { pbAuthedFetch } from "../authHeaders";
import { useTableControls } from '../hooks/useTableControls';
import { DataTable } from '../components/DataTable';
import type { Column } from '../hooks/useTableControls';

export function ATOPage() {
  const { data: systemsData, isLoading: sysLoading, error: sysError, refetch: refetchSystems } = useQuery({
    queryKey: ['ato-systems'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/ato/systems');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: boundaryData } = useQuery({
    queryKey: ['ato-boundary-reconciliation'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/ato/boundary-reconciliation');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: poamData, isLoading: poamLoading, error: poamError } = useQuery({
    queryKey: ['ato-poam'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/ato/poam');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const { data: runwayData, isLoading: runwayLoading, error: runwayError } = useQuery({
    queryKey: ['ato-expiry-runway'],
    queryFn: async () => {
      const res = await pbAuthedFetch('/api/ato/expiry-runway');
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      return res.json();
    },
  });

  const systems = systemsData?.systems || [];
  const boundary = boundaryData || {};
  const poamItems = poamData?.items || [];
  const runway = runwayData?.items || [];

  const claimedInside = boundary.claimed_inside || [];
  const actuallyObserved = boundary.actually_observed || [];
  const unassignedCount = boundary.unassigned_count ?? 0;

  const isLoading = sysLoading || poamLoading || runwayLoading;
  const hasError = sysError || poamError || runwayError;

  // System Inventory columns
  const systemColumns: Column<any>[] = [
    { key: 'system_name', label: 'System' },
    {
      key: 'authorization_status',
      label: 'Authorization Status',
      filterOptions: [
        { value: 'authorized', label: 'Authorized' },
        { value: 'expired', label: 'Expired' },
        { value: 'in_review', label: 'In Review' },
      ],
      render: (row: any) => (
        <span className={`pill pill--${row.authorization_status === 'authorized' ? 'ok' : row.authorization_status === 'expired' ? 'risk' : 'warn'}`}>
          <i></i>
          {row.authorization_status}
        </span>
      ),
    },
    { key: 'ato_expiry', label: 'ATO Expiry', dateField: true, render: (row: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{row.ato_expiry ? new Date(row.ato_expiry).toLocaleDateString() : '—'}</span> },
    { key: 'fips_199_level', label: 'FIPS 199', render: (row: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{row.fips_199_level || '—'}</span> },
    { key: 'system_owner', label: 'System Owner', render: (row: any) => row.system_owner || '—' },
    { key: 'isso', label: 'ISSO', render: (row: any) => row.isso || '—' },
    { key: 'ao', label: 'AO', render: (row: any) => row.ao || '—' },
  ];

  // POA&M Register columns
  const isOverdue = (row: any) =>
    row.scheduled_completion && new Date(row.scheduled_completion) < new Date() && row.status !== 'closed';

  const poamColumns: Column<any>[] = [
    {
      key: 'weakness',
      label: 'Weakness',
      render: (row: any) => (
        <span style={isOverdue(row) ? { background: 'var(--risk)', color: '#fff', padding: '2px 6px', borderRadius: 'var(--r-xs)' } : undefined}>
          {row.weakness}
        </span>
      ),
    },
    {
      key: 'source',
      label: 'Source',
      render: (row: any) => (
        <span style={isOverdue(row) ? { background: 'var(--risk)', color: '#fff', padding: '2px 6px', borderRadius: 'var(--r-xs)' } : undefined}>
          {row.source || '—'}
        </span>
      ),
    },
    {
      key: 'severity',
      label: 'Severity',
      filterOptions: [
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' },
      ],
      render: (row: any) => (
        <span style={isOverdue(row) ? { background: 'var(--risk)', color: '#fff', padding: '2px 6px', borderRadius: 'var(--r-xs)' } : undefined}>
          <span
            className={`pill pill--${row.severity === 'high' ? 'risk' : row.severity === 'low' ? 'ok' : 'warn'}`}
            style={isOverdue(row) ? { color: '#fff' } : undefined}
          >
            <i style={isOverdue(row) ? { background: '#fff' } : undefined}></i>
            {row.severity || '—'}
          </span>
        </span>
      ),
    },
    {
      key: 'scheduled_completion',
      label: 'Scheduled Completion',
      render: (row: any) => (
        <span style={{ ...(isOverdue(row) ? { background: 'var(--risk)', color: '#fff', padding: '2px 6px', borderRadius: 'var(--r-xs)' } : {}), fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>
          {row.scheduled_completion ? new Date(row.scheduled_completion).toLocaleDateString() : '—'}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      filterOptions: [
        { value: 'open', label: 'Open' },
        { value: 'closed', label: 'Closed' },
        { value: 'in_progress', label: 'In Progress' },
      ],
      render: (row: any) => (
        <span style={isOverdue(row) ? { background: 'var(--risk)', color: '#fff', padding: '2px 6px', borderRadius: 'var(--r-xs)' } : undefined}>
          {row.status || '—'}
        </span>
      ),
    },
    {
      key: 'responsible_party',
      label: 'Responsible Party',
      render: (row: any) => (
        <span style={isOverdue(row) ? { background: 'var(--risk)', color: '#fff', padding: '2px 6px', borderRadius: 'var(--r-xs)' } : undefined}>
          {row.responsible_party || '—'}
        </span>
      ),
    },
  ];

  // ATO Expiry Runway columns
  const runwayColumns: Column<any>[] = [
    { key: 'system_name', label: 'System' },
    { key: 'reauthorization_date', label: 'Reauthorization Date', dateField: true, render: (row: any) => <span style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-sm)' }}>{row.reauthorization_date ? new Date(row.reauthorization_date).toLocaleDateString() : '—'}</span> },
    {
      key: 'days_remaining',
      label: 'Days Remaining',
      render: (row: any) => {
        const days = row.days_remaining ?? 999;
        const variant = days <= 30 ? 'risk' : days <= 90 ? 'warn' : 'ok';
        return (
          <span className={`pill pill--${variant}`}>
            <i></i>
            {row.days_remaining != null ? `${row.days_remaining}d` : '—'}
          </span>
        );
      },
    },
  ];

  const systemsTable = useTableControls(systems, systemColumns, { storageKey: 'ato-systems' });
  const poamTable = useTableControls(poamItems, poamColumns, { storageKey: 'ato-poam' });
  const runwayTable = useTableControls(runway, runwayColumns, { storageKey: 'ato-runway' });

  // Loading state
  if (isLoading) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Compliance</p>
              <h1 className="pagehead__title">ATO Posture &amp; POA&amp;M</h1>
              <div className="pagehead__facts">
                <span className="fact">Authorization status &amp; boundary reconciliation</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="page-loading" role="status" aria-live="polite">
                Loading ATO data…
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (hasError) {
    return (
      <>
        <div className="pagehead">
          <div className="pagehead__row">
            <div className="pagehead__text">
              <p className="pagehead__eyebrow">Compliance</p>
              <h1 className="pagehead__title">ATO Posture &amp; POA&amp;M</h1>
              <div className="pagehead__facts">
                <span className="fact">Authorization status &amp; boundary reconciliation</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pagebody">
          <div className="box">
            <div className="box__body">
              <div className="state state--risk">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <h3 className="state__title">Failed to load ATO data</h3>
                <p className="state__body">
                  Could not connect to the ATO API. Please check your connection and try again.
                </p>
                <button className="btn btn--primary" onClick={() => refetchSystems()}>
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">Compliance</p>
            <h1 className="pagehead__title">ATO Posture &amp; POA&amp;M</h1>
            <div className="pagehead__facts">
              <span className="fact">Authorization status, plan of actions &amp; milestones, and boundary reconciliation</span>
              <span className="fact">Systems: <b>{systems.length}</b></span>
              <span className="fact">Open POA&amp;M: <b>{poamItems.filter((p: any) => p.status !== 'closed').length}</b></span>
            </div>
          </div>
        </div>
      </div>

      <div className="pagebody">
        {/* Boundary Membership Reconciliation — indicators */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--sp-4)' }} role="group" aria-label="Boundary reconciliation summary">
          <div className="ind ind--watch">
            <div className="ind__head">
              <p className="ind__abbr">Claimed</p>
              <p className="ind__full">Claimed Inside Boundary</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{claimedInside.length}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--gold)' }}></i>
              <b style={{ color: 'var(--ink)' }}>CIs</b> asserted as in-bound
            </p>
          </div>

          <div className="ind ind--watch">
            <div className="ind__head">
              <p className="ind__abbr">Observed</p>
              <p className="ind__full">Actually Observed</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{actuallyObserved.length}</p>
            </div>
            <p className="ind__reading">
              <i style={{ background: 'var(--ok)' }}></i>
              <b style={{ color: 'var(--ink)' }}>CIs</b> detected inside boundary
            </p>
          </div>

          <div className={`ind ${unassignedCount > 0 ? 'ind--breach' : 'ind--watch'}`}>
            <div className="ind__head">
              <p className="ind__abbr">Unassigned</p>
              <p className="ind__full">Unassigned CIs</p>
            </div>
            <div className="ind__value">
              <p className="ind__v">{unassignedCount}</p>
            </div>
            <p className="ind__reading">
              <i></i>
              {unassignedCount > 0 ? (
                <><b>Action required</b> — not mapped to any system</>
              ) : (
                <><b style={{ color: 'var(--ok)' }}>Clear</b> — all CIs mapped</>
              )}
            </p>
          </div>
        </div>

        {/* System Inventory */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Authorization</p>
              <h2 className="box__title">System Inventory</h2>
            </div>
          </div>
          <div className="box__body">
            {systems.length > 0 ? (
              <DataTable
                columns={systemColumns}
                visibleColumns={systemsTable.visibleColumns}
                data={systemsTable.paged}
                search={systemsTable.search}
                onSearchChange={systemsTable.setSearch}
                sortKey={systemsTable.sortKey}
                sortDir={systemsTable.sortDir}
                onSort={systemsTable.setSort}
                page={systemsTable.page}
                totalPages={systemsTable.totalPages}
                totalFiltered={systemsTable.totalFiltered}
                pageSize={systemsTable.pageSize}
                onPageChange={systemsTable.setPage}
                onPageSizeChange={systemsTable.setPageSize}
                activeFilters={systemsTable.activeFilters}
                onFilter={systemsTable.setFilter}
                onClearFilters={systemsTable.clearFilters}
                searchPlaceholder="Search systems..."
                keyAccessor={(row: any) => row.system_name}
                emptyMessage="No systems reported."
                timeRange={systemsTable.timeRange}
                onTimeRangeChange={systemsTable.setTimeRange}
                hiddenColumns={systemsTable.hiddenColumns}
                onToggleColumn={systemsTable.toggleColumn}
                selectedIds={systemsTable.selectedIds}
                onToggleSelect={systemsTable.toggleSelect}
                onSelectAll={systemsTable.selectAll}
                onClearSelection={systemsTable.clearSelection}
                savedViews={systemsTable.savedViews}
                onSaveView={systemsTable.saveView}
                onLoadView={systemsTable.loadView}
                onDeleteView={systemsTable.deleteView}
                onExportCSV={systemsTable.exportCSV}
                onExportExcel={systemsTable.exportExcel}
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
                <h3 className="state__title">No systems reported</h3>
                <p className="state__body">
                  No system inventory data is available. Configure your ATO sources to begin tracking.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* POA&M Register */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Findings</p>
              <h2 className="box__title">POA&amp;M Register</h2>
            </div>
          </div>
          <div className="box__body">
            {poamItems.length > 0 ? (
              <DataTable
                columns={poamColumns}
                visibleColumns={poamTable.visibleColumns}
                data={poamTable.paged}
                search={poamTable.search}
                onSearchChange={poamTable.setSearch}
                sortKey={poamTable.sortKey}
                sortDir={poamTable.sortDir}
                onSort={poamTable.setSort}
                page={poamTable.page}
                totalPages={poamTable.totalPages}
                totalFiltered={poamTable.totalFiltered}
                pageSize={poamTable.pageSize}
                onPageChange={poamTable.setPage}
                onPageSizeChange={poamTable.setPageSize}
                activeFilters={poamTable.activeFilters}
                onFilter={poamTable.setFilter}
                onClearFilters={poamTable.clearFilters}
                searchPlaceholder="Search POA&M items..."
                keyAccessor={(row: any) => row.id || row.weakness}
                emptyMessage="No POA&M items reported."
                timeRange={poamTable.timeRange}
                onTimeRangeChange={poamTable.setTimeRange}
                hiddenColumns={poamTable.hiddenColumns}
                onToggleColumn={poamTable.toggleColumn}
                selectedIds={poamTable.selectedIds}
                onToggleSelect={poamTable.toggleSelect}
                onSelectAll={poamTable.selectAll}
                onClearSelection={poamTable.clearSelection}
                savedViews={poamTable.savedViews}
                onSaveView={poamTable.saveView}
                onLoadView={poamTable.loadView}
                onDeleteView={poamTable.deleteView}
                onExportCSV={poamTable.exportCSV}
                onExportExcel={poamTable.exportExcel}
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
                <h3 className="state__title">No POA&M items</h3>
                <p className="state__body">
                  No plan of actions &amp; milestones have been reported.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ATO Expiry Runway */}
        <div className="box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Expiry</p>
              <h2 className="box__title">ATO Expiry Runway</h2>
            </div>
          </div>
          <div className="box__body">
            {runway.length > 0 ? (
              <DataTable
                columns={runwayColumns}
                visibleColumns={runwayTable.visibleColumns}
                data={runwayTable.paged}
                search={runwayTable.search}
                onSearchChange={runwayTable.setSearch}
                sortKey={runwayTable.sortKey}
                sortDir={runwayTable.sortDir}
                onSort={runwayTable.setSort}
                page={runwayTable.page}
                totalPages={runwayTable.totalPages}
                totalFiltered={runwayTable.totalFiltered}
                pageSize={runwayTable.pageSize}
                onPageChange={runwayTable.setPage}
                onPageSizeChange={runwayTable.setPageSize}
                activeFilters={runwayTable.activeFilters}
                onFilter={runwayTable.setFilter}
                onClearFilters={runwayTable.clearFilters}
                searchPlaceholder="Search runway..."
                keyAccessor={(row: any) => row.system_name}
                emptyMessage="No upcoming reauthorizations."
                timeRange={runwayTable.timeRange}
                onTimeRangeChange={runwayTable.setTimeRange}
                hiddenColumns={runwayTable.hiddenColumns}
                onToggleColumn={runwayTable.toggleColumn}
                selectedIds={runwayTable.selectedIds}
                onToggleSelect={runwayTable.toggleSelect}
                onSelectAll={runwayTable.selectAll}
                onClearSelection={runwayTable.clearSelection}
                savedViews={runwayTable.savedViews}
                onSaveView={runwayTable.saveView}
                onLoadView={runwayTable.loadView}
                onDeleteView={runwayTable.deleteView}
                onExportCSV={runwayTable.exportCSV}
                onExportExcel={runwayTable.exportExcel}
              />
            ) : (
              <div className="state">
                <svg className="state__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
                <h3 className="state__title">No upcoming reauthorizations</h3>
                <p className="state__body">
                  No ATO expiry data is available. Configure reauthorization tracking to see upcoming deadlines.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
